import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
	trackSequentialAnswerSubmitted,
	trackSequentialInterviewCompleted,
	trackSequentialInterviewResumed,
	trackSequentialInterviewStarted,
	trackSequentialQuestionPlayed,
} from "@/lib/analytics-events";
import { apiService } from "@/lib/api-service";
import { useElevenLabsSTT } from "./useElevenLabsSTT";

// ── Types ───────────────────────────────────────────────────────────────

export type InterviewState =
	| "idle"
	| "loading"
	| "waiting_for_answer"
	| "recording"
	| "submitting"
	| "complete";

export interface QuestionItem {
	id: number;
	question_text: string;
	category: string;
	order: number;
	phase: number;
	question_type: string;
	parent_question_id: number | null;
}

interface InterviewStatusResponse {
	total_questions: number;
	current_question_index: number;
	is_completed: boolean;
	questions: QuestionItem[];
	saved_answers: { question_index: number; answer_text: string }[];
	session_id: string | null;
	interview_id: string | null;
	question_path: number[];
}

interface StartInterviewResponse {
	interview_id: string;
	session_id: string;
	campaign_id: number;
	status: string;
	interview_mode: string;
	current_question_index: number;
	total_questions: number;
	message: string;
}

interface SaveAnswerResponse {
	question_index: number;
	current_question_index: number;
	total_questions: number;
	is_last_question: boolean;
	message: string;
	next_questions: QuestionItem[];
	question_path: number[];
}

interface CompleteInterviewResponse {
	interview_id: string;
	session_id: string;
	status: string;
	completed_at: string;
	interview_completed: boolean;
	total_answers: number;
	message: string;
}

export interface UseSequentialInterviewReturn {
	// State
	state: InterviewState;
	currentQuestionIndex: number;
	totalQuestions: number;
	questions: QuestionItem[];
	questionsMap: Map<number, QuestionItem>;
	savedAnswers: Map<number, string>;
	isCompleted: boolean;
	questionPath: number[];
	currentQuestion: QuestionItem | undefined;

	// TTS (kept for interface compat — always false/0)
	isPlaying: boolean;
	visibleWordCount: number;

	// Actions
	startInterview: () => void;
	playQuestion: (index: number) => void;
	submitAnswer: (text: string, source: "voice" | "text") => void;
	goToQuestion: (index: number) => void;
	skipQuestion: () => void;
	goBack: () => void;
	completeInterview: () => void;
	pauseInterview: () => void;

	// Recording
	isRecording: boolean;
	transcript: string;
	interimTranscript: string;
	startRecording: () => void;
	stopRecording: () => void;
	isSpeechSupported: boolean;
	sttError: string | null;

	// Error
	error: string | null;
}

// ── Main hook ───────────────────────────────────────────────────────────

export function useSequentialInterview(
	campaignSlug: string | undefined,
	_firstName?: string,
): UseSequentialInterviewReturn {
	const queryClient = useQueryClient();
	const [state, setState] = useState<InterviewState>("idle");
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
	const [totalQuestions, setTotalQuestions] = useState(0);
	const [questions, setQuestions] = useState<QuestionItem[]>([]);
	const [savedAnswers, setSavedAnswers] = useState<Map<number, string>>(
		new Map(),
	);
	const [isCompleted, setIsCompleted] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [questionPath, setQuestionPath] = useState<number[]>([]);

	// Refs
	const isMountedRef = useRef(true);
	const sessionIdRef = useRef<string | null>(null);

	// Speech recognition (ElevenLabs Scribe)
	const {
		isSupported: isSpeechSupported,
		isListening: isRecording,
		transcript,
		interimTranscript,
		startListening,
		stopListening,
		resetTranscript,
		error: sttError,
	} = useElevenLabsSTT({ campaignSlug });

	// Cleanup on unmount
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// ── Derived: question lookup by ID ──────────────────────────────────

	const questionsMap = useMemo(() => {
		const map = new Map<number, QuestionItem>();
		for (const q of questions) {
			map.set(q.id, q);
		}
		return map;
	}, [questions]);

	// Resolve current question via questionPath → questionsMap
	const currentQuestion = useMemo(() => {
		const qid = questionPath[currentQuestionIndex];
		return qid !== undefined ? questionsMap.get(qid) : questions[currentQuestionIndex];
	}, [questionPath, currentQuestionIndex, questionsMap, questions]);

	// ── Show question (no TTS — just transition to waiting) ─────────────

	const showQuestion = useCallback(
		(index: number) => {
			const qid = questionPath[index];
			const question = qid !== undefined ? questionsMap.get(qid) : questions[index];
			if (!question) return;
			resetTranscript();
			setState("waiting_for_answer");
			trackSequentialQuestionPlayed(index, question.category);
		},
		[questionPath, questionsMap, questions, resetTranscript],
	);

	// ── Fetch status on mount (resume support) ──────────────────────────

	useEffect(() => {
		if (!campaignSlug) return;

		const fetchStatus = async () => {
			try {
				setState("loading");
				const status = (await apiService.getSequentialInterviewStatus(
					campaignSlug,
				)) as InterviewStatusResponse;

				if (!isMountedRef.current) return;

				setQuestions(status.questions);
				setTotalQuestions(status.total_questions);
				if (status.question_path) {
					setQuestionPath(status.question_path);
				}

				if (status.is_completed) {
					setIsCompleted(true);
					setState("complete");
					return;
				}

				if (status.session_id && status.saved_answers.length > 0) {
					// Resume existing session
					sessionIdRef.current = status.session_id;
					const answersMap = new Map<number, string>();
					for (const a of status.saved_answers) {
						answersMap.set(a.question_index, a.answer_text);
					}
					setSavedAnswers(answersMap);
					setCurrentQuestionIndex(status.current_question_index);
					trackSequentialInterviewResumed(status.saved_answers.length);
					setState("waiting_for_answer");
				} else {
					setState("idle");
				}
			} catch (err) {
				console.error("[SequentialInterview] Failed to fetch status:", err);
				if (isMountedRef.current) {
					// If status endpoint fails (e.g., no session yet), stay at idle
					setState("idle");
				}
			}
		};

		fetchStatus();
	}, [campaignSlug]);

	// ── Actions ─────────────────────────────────────────────────────────

	const startInterview = useCallback(async () => {
		if (!campaignSlug) return;

		try {
			setState("loading");
			setError(null);

			// Ensure questions are loaded (may be empty if initial fetch failed)
			if (questions.length === 0) {
				const status = (await apiService.getSequentialInterviewStatus(
					campaignSlug,
				)) as InterviewStatusResponse;
				if (!isMountedRef.current) return;
				setQuestions(status.questions);
				setTotalQuestions(status.total_questions);
			}

			const result = (await apiService.startSequentialInterview(
				campaignSlug,
			)) as StartInterviewResponse;

			if (!isMountedRef.current) return;

			sessionIdRef.current = result.session_id;
			setCurrentQuestionIndex(result.current_question_index);
			setTotalQuestions(result.total_questions);

			trackSequentialInterviewStarted();

			// Go straight to the first question (no intro screen)
			// Set state directly — showQuestion's closure may have stale questions
			resetTranscript();
			setState("waiting_for_answer");
		} catch (err) {
			console.error("[SequentialInterview] Failed to start:", err);
			if (isMountedRef.current) {
				setError(
					err instanceof Error ? err.message : "Failed to start interview",
				);
				setState("idle");
			}
		}
	}, [campaignSlug, questions.length, resetTranscript]);

	const playQuestion = useCallback(
		(index: number) => {
			setCurrentQuestionIndex(index);
			showQuestion(index);
		},
		[showQuestion],
	);

	const completeInterviewInternal = useCallback(async () => {
		if (!campaignSlug) return;

		try {
			setError(null);

			const result = (await apiService.completeSequentialInterview(
				campaignSlug,
			)) as CompleteInterviewResponse;

			if (!isMountedRef.current) return;

			trackSequentialInterviewCompleted(result.total_answers);
			setIsCompleted(true);
			setState("complete");
			queryClient.invalidateQueries({ queryKey: ["session"] });
		} catch (err) {
			console.error("[SequentialInterview] Failed to complete:", err);
			if (isMountedRef.current) {
				setError(
					err instanceof Error ? err.message : "Failed to complete interview",
				);
				setState("waiting_for_answer");
			}
		}
	}, [campaignSlug]);

	const completeInterview = completeInterviewInternal;

	const pauseInterview = useCallback(async () => {
		if (!campaignSlug) return;
		try {
			await apiService.pauseSequentialInterview(campaignSlug);
		} catch (err) {
			console.error("[SequentialInterview] Failed to pause:", err);
		}
		// Navigate to dashboard regardless — progress is auto-saved
		window.location.href = "/";
	}, [campaignSlug]);

	const submitAnswer = useCallback(
		async (text: string, source: "voice" | "text") => {
			if (!campaignSlug || !text.trim()) return;

			// Stop recording before async save (AME-334/338)
			stopListening();
			resetTranscript();

			try {
				setState("submitting");
				setError(null);

				// Optimistic update
				setSavedAnswers((prev) => {
					const next = new Map(prev);
					next.set(currentQuestionIndex, text);
					return next;
				});

				trackSequentialAnswerSubmitted(currentQuestionIndex, source);

				const result = (await apiService.saveSequentialAnswer(campaignSlug, {
					question_index: currentQuestionIndex,
					answer_text: text,
					answer_source: source,
				})) as SaveAnswerResponse;

				if (!isMountedRef.current) return;

				// Handle follow-up question injection
				if (result.next_questions && result.next_questions.length > 0) {
					setQuestions((prev) => {
						const existingIds = new Set(prev.map((q) => q.id));
						const newQuestions = result.next_questions.filter(
							(q) => !existingIds.has(q.id),
						);
						return [...prev, ...newQuestions];
					});
				}

				// Update question path and total count
				if (result.question_path) {
					setQuestionPath(result.question_path);
				}
				setTotalQuestions(result.total_questions);

				if (result.is_last_question) {
					// Auto-complete on last question — no review step
					await completeInterviewInternal();
				} else {
					const nextIndex = result.current_question_index;
					setCurrentQuestionIndex(nextIndex);
					showQuestion(nextIndex);
				}
			} catch (err) {
				console.error("[SequentialInterview] Failed to save answer:", err);
				if (isMountedRef.current) {
					setError(
						err instanceof Error ? err.message : "Failed to save answer",
					);
					setState("waiting_for_answer");
				}
			}
		},
		[
			campaignSlug,
			currentQuestionIndex,
			stopListening,
			resetTranscript,
			showQuestion,
			completeInterviewInternal,
		],
	);

	const goToQuestion = useCallback(
		(index: number) => {
			if (index < 0 || index >= totalQuestions) return;
			resetTranscript();
			setCurrentQuestionIndex(index);
			setState("waiting_for_answer");
		},
		[totalQuestions, resetTranscript],
	);

	const skipQuestion = useCallback(() => {
		if (currentQuestionIndex >= totalQuestions - 1) {
			// On last question, auto-complete
			completeInterviewInternal();
		} else {
			goToQuestion(currentQuestionIndex + 1);
		}
	}, [currentQuestionIndex, totalQuestions, goToQuestion, completeInterviewInternal]);

	const goBack = useCallback(() => {
		if (currentQuestionIndex > 0) {
			goToQuestion(currentQuestionIndex - 1);
		}
	}, [currentQuestionIndex, goToQuestion]);

	const startRecording = useCallback(() => {
		resetTranscript();
		startListening();
		setState("recording");
	}, [startListening, resetTranscript]);

	const stopRecordingFn = useCallback(() => {
		stopListening();
		setState("waiting_for_answer");
	}, [stopListening]);

	return {
		// State
		state,
		currentQuestionIndex,
		totalQuestions,
		questions,
		questionsMap,
		savedAnswers,
		isCompleted,
		questionPath,
		currentQuestion,

		// TTS (kept for interface compat — always false/0)
		isPlaying: false,
		visibleWordCount: 0,

		// Actions
		startInterview,
		playQuestion,
		submitAnswer,
		goToQuestion,
		skipQuestion,
		goBack,
		completeInterview,
		pauseInterview,

		// Recording
		isRecording,
		transcript,
		interimTranscript,
		startRecording,
		stopRecording: stopRecordingFn,
		isSpeechSupported,
		sttError,

		// Error
		error,
	};
}
